/**
 * A room, or a garment, built the way she is.
 *
 * The same four steps her identity set has — photos, build, review, keep — and
 * deliberately the same markup, so anyone who has made a character has already
 * made both. The one difference is the yield: her four references exist because
 * a face has to be seen from four sides, and neither a room nor a garment does.
 * Photograph the parts; the build composes them into ONE photograph, which is
 * the single image a beat spends that slot on.
 *
 * Order is not cosmetic here either. The prompt numbers the photos, so photo 1
 * decides which corner a room ends up framed from.
 *
 * Rooms and garments differ in three places and nowhere else: the words, the
 * two or three fields under the name, and what a build costs. Everything below
 * is driven by SUBJECTS rather than duplicated per screen.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Photo } from '../lib/api';
import { money } from '../lib/compose';
import Img from '../components/Img';
import './characters.css';

/** Nine, the same ceiling the API holds. */
const MAX_PHOTOS = 9;

/** A field under the name. Rooms have two, a garment has one. */
interface FieldSpec { key: string; label: string; placeholder: string; rows?: number; max: number }

interface Subject {
  steps: readonly [string, string, string, string];
  /** What the thing is, in the sentence "Photos of the ___". */
  noun: string;
  fields: FieldSpec[];
  /** What one build costs before the per-photo extra. */
  cost: number;
  /** How long a build takes, in words. */
  takes: string;
  buildTitle: string;
  buildSub: string;
  buildShape: string;
  photoHint: React.ReactNode;
  reviewHint: string;
  warning: React.ReactNode;
  /** Placeholder and guidance for the per-build note. */
  notePlaceholder: string;
  noteHint: string;
  /** The class that gives the result its aspect ratio. */
  frame: string;
}

const SUBJECTS: Record<'room' | 'garment', Subject> = {
  room: {
    steps: ['Photos', 'Build the room', 'Review', 'The room'],
    noun: 'the room',
    fields: [
      { key: 'kind', label: 'Kind', placeholder: 'kitchen', max: 30 },
      { key: 'description', label: "Anything the photos don't show", rows: 4, max: 600,
        placeholder: 'Yellow 70s tiles. The garden is visible through the window over the sink.' },
    ],
    cost: 0.09,
    takes: '90–140s',
    buildTitle: 'The room, in one frame',
    buildSub: 'from a corner, ultra-wide, straight and level — two walls, the floor, the ceiling line',
    buildShape: '2K · 4:3',
    photoHint: (
      <>
        <b>Photograph the parts, not one perfect wide shot.</b> The corner, the counter, the
        window — several ordinary photos of the same room tell the build more than one careful
        one, and putting them together is its job, not yours. With no photos at all this still
        works; the details are invented rather than copied.
      </>
    ),
    reviewHint: 'The small tile is the first photo it was built from. Re-roll it if it drifted — '
      + 'this is the room every set that picks it will be standing in.',
    warning: (
      <>
        <b>Parts of the room your photos don't show are invented.</b> If you only photographed one
        end of it, the rest is plausible and internally consistent, but it is not derived from
        anything you gave us. Check it properly — every set that picks this room inherits it.
      </>
    ),
    notePlaceholder: 'e.g. from the doorway instead, nothing on the counter',
    noteHint: 'Use it to correct what the photos cannot say — where to stand, what to leave out, '
      + 'the hour of the light.',
    frame: 'wide',
  },
  garment: {
    steps: ['Photos', 'Build the garment', 'Review', 'The garment'],
    noun: 'the garment',
    fields: [
      { key: 'garment', label: 'What it is', rows: 3, max: 200,
        placeholder: 'Cropped ribbed vest and matching shorts, soft elastic waist, worn in.' },
    ],
    cost: 0.045,
    takes: '~40s',
    buildTitle: 'The garment, laid flat',
    buildSub: 'flat lay on a plain pale background — the whole piece, nobody wearing it',
    buildShape: '1K · 1:1',
    photoHint: (
      <>
        <b>Any photos of it will do — a product shot, or your own.</b> Several of the same piece
        beat one careful one. With none, the words carry it and the build invents the details.
      </>
    ),
    reviewHint: 'The small tile is the first photo it was built from. Re-roll it if it came back '
      + 'as something else — every beat that asks for this slot gets this picture.',
    warning: (
      <>
        <b>Nobody wears it, and that is the point.</b> A garment photographed on a model brings
        that model's face, hands and body into every render that later cites the picture. Check
        that nothing of a person survived in it.
      </>
    ),
    notePlaceholder: 'e.g. no logo, sleeves laid flat, seen from directly above',
    noteHint: 'Use it for what a photo of the piece does not settle — how it is laid out, a print '
      + 'to leave off, the colour it should actually be.',
    frame: 'square',
  },
};

/** What the wizard needs of a room or a slot. Both routes already return it. */
export interface Buildable {
  id: string;
  name: string;
  src: string | null;
  status: 'running' | 'done' | 'failed';
  error: string | null;
  approved: boolean;
  photos: Photo[];
  usedCount?: number;
  /** Steers one build; kept between re-rolls. */
  note?: string;
  /** kind/description for a room, garment for a slot. */
  extra: Record<string, string>;
}

export interface BuildWizardProps {
  kind: 'room' | 'garment';
  /** '/characters/<id>/places' or '/characters/<id>/wardrobe'. */
  collection: string;
  item: Buildable;
  /** Reload the caller's list — this component holds no copy of the item. */
  onChanged: () => Promise<void> | void;
  onClose: () => void;
  closeLabel: string;
}

export default function BuildWizard({
  kind, collection, item, onChanged, onClose, closeLabel,
}: BuildWizardProps) {
  const subject = SUBJECTS[kind];
  const place = item;
  const STEPS = subject.steps;
  const [step, setStep] = useState(place.approved ? 4 : place.src ? 3 : 1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileOver, setFileOver] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ src: string | null; label: string } | null>(null);

  const [name, setName] = useState(place.name);
  const [extra, setExtra] = useState<Record<string, string>>(place.extra);
  const [note, setNote] = useState(place.note ?? '');
  const fileRef = useRef<HTMLInputElement>(null);
  const directRef = useRef<HTMLInputElement>(null);

  const base = `${collection}/${place.id}`;
  const photos = place.photos;
  const building = place.status === 'running';

  const guard = useCallback(async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }, [onChanged]);

  // A finished build is the moment to look at it, so the step moves itself —
  // the build takes minutes and nobody should have to sit on this screen.
  useEffect(() => {
    if (step === 2 && place.status === 'done' && place.src && !place.approved) setStep(3);
  }, [step, place.status, place.src, place.approved]);

  const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types ?? []).includes('Files');

  function addFiles(files: FileList | File[]) {
    const room = Math.max(0, MAX_PHOTOS - photos.length);
    // A live FileList empties when the input is reset, so copy it first.
    const images = Array.from(files).filter((f) => f.type.startsWith('image/')).slice(0, room);
    if (images.length === 0) return;
    void guard(async () => {
      for (const file of images) {
        const form = new FormData();
        form.append('file', file);
        await api.upload(`${base}/photos`, form);
      }
    });
  }

  function move(fromId: string, toId: string) {
    if (fromId === toId) return;
    const ids = photos.map((p) => p.id);
    const from = ids.indexOf(fromId);
    const to = ids.indexOf(toId);
    if (from < 0 || to < 0) return;
    const next = [...photos];
    next.splice(to, 0, ...next.splice(from, 1));
    void guard(() => api.patch(`${base}/photos/order`, { ids: next.map((p) => p.id) }));
  }

  function nudge(id: string, dir: -1 | 1) {
    const ids = photos.map((p) => p.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    move(id, ids[j]!);
  }

  const saveFields = () =>
    guard(() => api.patch(`${base}`, { name: name.trim() || place.name, ...extra }));

  const build = () => guard(() => api.post(`${base}/build`, { note }));
  const keep = () => guard(() => api.post(`${base}/approve`));

  const cost = subject.cost + Math.max(0, photos.length - 1) * 0.003;
  const reachable = (n: number) => n === 1 || n === 2 || (n === 3 && Boolean(place.src)) || (n === 4 && place.approved);

  return (
    <div className="card">
      <div className="world-head">
        <div>
          <span className="eyebrow">{place.name || (kind === 'room' ? 'New room' : 'New slot')}</span>
          <p className="muted">
            Photos of it, one build, one look at it, and it's hers. Nobody is in the picture —
            she is put with it at render time.
          </p>
        </div>
        <button className="btn btn-sm" onClick={onClose}>{closeLabel}</button>
      </div>

      <ol className="steps">
        {STEPS.map((label, i) => {
          const n = i + 1;
          return (
            <li key={label}>
              <button
                className={n < step ? 'done' : undefined}
                disabled={!reachable(n)}
                {...(n === step ? { 'aria-current': 'step' as const } : {})}
                onClick={() => reachable(n) && setStep(n)}
              >
                <span className="n">{n < step ? '✓' : n}</span>{label}
              </button>
            </li>
          );
        })}
      </ol>

      {error && <div className="notice bad" style={{ marginBottom: 16 }}>{error}</div>}

      {/* ─────────── 1 · photos ─────────── */}
      {step === 1 && (
        <div className="wiz-split">
          <div className="card">
            <div className="sect-head">
              <span className="eyebrow">Photos of {subject.noun}</span>
              <span className="pill mono" style={{ fontSize: 10.5 }}>
                optional · {photos.length} of {MAX_PHOTOS}
              </span>
            </div>

            <div
              className={`src-grid${fileOver ? ' file-over' : ''}`}
              onDragOver={(e) => { if (!isFileDrag(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setFileOver(true); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFileOver(false); }}
              onDrop={(e) => { if (!isFileDrag(e)) return; e.preventDefault(); setFileOver(false); addFiles(e.dataTransfer.files); }}
            >
              {photos.map((photo, i) => (
                <div
                  key={photo.id}
                  className={[
                    'src-tile', i === 0 ? 'lead' : '',
                    dragId === photo.id ? 'dragging' : '',
                    dropId === photo.id && dragId !== photo.id ? 'dropping' : '',
                  ].filter(Boolean).join(' ')}
                  draggable tabIndex={0} title="Drag to reorder"
                  onDragStart={(e) => {
                    setDragId(photo.id);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', photo.id);
                  }}
                  onDragOver={(e) => {
                    if (isFileDrag(e)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDropId(photo.id);
                  }}
                  onDragLeave={() => setDropId((k) => (k === photo.id ? null : k))}
                  onDrop={(e) => {
                    if (isFileDrag(e)) return;
                    e.preventDefault();
                    if (dragId) move(dragId, photo.id);
                    setDragId(null); setDropId(null);
                  }}
                  onDragEnd={() => { setDragId(null); setDropId(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(photo.id, -1); }
                    if (e.key === 'ArrowRight') { e.preventDefault(); nudge(photo.id, 1); }
                  }}
                >
                  <button
                    className="peek" title="See it full size"
                    onClick={() => setPreview({ src: photo.src, label: `Photo ${i + 1}` })}
                  >
                    <Img src={photo.src} alt={`Photo ${i + 1}`} />
                  </button>
                  <span className="idx">{i + 1}</span>
                  <button
                    className="kill" title="Remove" disabled={busy}
                    onClick={(e) => { e.stopPropagation(); void guard(() => api.del(`${base}/photos/${photo.id}`)); }}
                  >×</button>
                  {i === 0 && <span className="tag">leads</span>}
                </div>
              ))}

              {photos.length < MAX_PHOTOS && (
                <div className="src-add">
                  <button className="main" disabled={busy} onClick={() => fileRef.current?.click()}>
                    {busy ? <span className="spin" /> : <span style={{ fontSize: 21 }}>+</span>}
                    <span>{busy ? 'Adding…' : 'Add photo'}</span>
                    <span className="sub">drag in, or browse<br />your machine</span>
                  </button>
                </div>
              )}
            </div>

            <p className="muted" style={{ margin: '12px 0 0', fontSize: 13, maxWidth: '74ch' }}>
              <b>Photograph the parts, not one perfect wide shot.</b> The corner, the counter, the
              window — several ordinary photos of the same room tell the build more than one careful
              one, and putting them together is its job, not yours. With no photos at all this still
              works; the details are invented rather than copied.
            </p>
          </div>

          <div className="card">
            <div className="sect-head"><span className="eyebrow">Details</span></div>
            <div className="wiz-field">
              <label className="eyebrow" htmlFor="bw-name">Name</label>
              <input
                id="bw-name" className="field" value={name} maxLength={60}
                placeholder={kind === 'room' ? "Her nan's kitchen" : 'sleepwear'}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            {subject.fields.map((f) => (
              <div className="wiz-field" key={f.key}>
                <label className="eyebrow" htmlFor={`bw-${f.key}`}>{f.label}</label>
                {f.rows
                  ? (
                    <textarea
                      id={`bw-${f.key}`} className="field" rows={f.rows} maxLength={f.max}
                      value={extra[f.key] ?? ''} placeholder={f.placeholder}
                      onChange={(e) => setExtra((x) => ({ ...x, [f.key]: e.target.value }))}
                    />
                  )
                  : (
                    <input
                      id={`bw-${f.key}`} className="field" maxLength={f.max}
                      value={extra[f.key] ?? ''} placeholder={f.placeholder}
                      onChange={(e) => setExtra((x) => ({ ...x, [f.key]: e.target.value }))}
                    />
                  )}
              </div>
            ))}
            <div className="foot">
              <button
                className="btn btn-primary" disabled={busy || !name.trim()}
                onClick={() => void saveFields().then(() => setStep(2))}
              >Save and continue</button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────── 2 · build ─────────── */}
      {step === 2 && (
        <div className="card">
          <div className="sect-head">
            <span className="eyebrow">{STEPS[1]}</span>
            <span className="pill mono" style={{ fontSize: 10.5 }}>one photograph</span>
          </div>

          <div className={`ref-row${place.status === 'done' && place.src ? ' done' : place.status === 'failed' ? ' failed' : ''}`}>
            <div className={`ref-main ${subject.frame}`}>
              <button
                className="thumb out" title={place.src ? 'See it full size' : undefined}
                disabled={!place.src}
                onClick={() => place.src && setPreview({ src: place.src, label: place.name })}
              >
                {place.src
                  ? <Img src={place.src} alt={place.name} />
                  : <span className="frame" aria-hidden="true" />}
              </button>
              <div className="info">
                <div className="nm">{subject.buildTitle}</div>
                <div className="sub">
                  {place.status === 'failed'
                    ? (place.error ?? 'that build failed')
                    : subject.buildSub}
                </div>
                <div className="inputs">
                  {photos.length > 0
                    ? `${photos.length} photo${photos.length === 1 ? '' : 's'} + words`
                    : 'words alone'} &rarr; 1 photograph · {subject.buildShape}
                </div>
              </div>
              <button
                className={`btn btn-sm${place.src ? '' : ' btn-primary'}`}
                disabled={busy || building}
                onClick={() => void build()}
              >
                {building ? 'Building…' : place.src ? 'Re-roll' : 'Build'}
              </button>
              <div className="t cell" style={place.src && !building ? { color: 'var(--fd-ok)' } : undefined}>
                {building ? 'building' : place.status === 'failed' ? 'failed' : place.src ? 'ready' : 'not built'}
              </div>
              {/* Steers this one build. Her references have had this since the
                  start and it is the most useful control on that screen. */}
              <div className="ref-note">
                <label className="eyebrow" htmlFor="bw-note">Note</label>
                <input
                  id="bw-note" value={note} maxLength={300} disabled={building}
                  placeholder={subject.notePlaceholder}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="notice" style={{ marginTop: 14 }}>
            <b>The note steers this one build only.</b> {subject.noteHint} It is kept, so a re-roll
            repeats it until you change it — and it is not the {kind === 'room' ? 'room' : 'garment'}'s
            description, which travels into every set that picks it.
          </div>

          <div className="foot">
            {place.src && !building && (
              <button className="btn btn-primary" onClick={() => setStep(3)}>Review it</button>
            )}
            <button className="btn" onClick={() => setStep(1)}>Back</button>
            {/* The free path, kept: a real photograph of a real room needs no
                build and no review — nothing about it was invented. */}
            <button className="btn btn-ghost" disabled={busy || building} onClick={() => directRef.current?.click()}>
              Use a photo as {subject.noun}
            </button>
            <span className="faint" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
              {subject.takes} · {money(cost)}
            </span>
          </div>
        </div>
      )}

      {/* ─────────── 3 · review ─────────── */}
      {step === 3 && (
        <div className="card">
          <div className="world-head">
            <div>
              <span className="eyebrow">Review</span>
              <p className="muted" style={{ margin: '7px 0 0', fontSize: 13, maxWidth: '74ch' }}>
{subject.reviewHint}
              </p>
            </div>
            <span className={`pill${place.approved ? ' ok' : ''}`}>
              <span className="dot" />{place.approved ? 'kept' : 'not kept yet'}
            </span>
          </div>

          <div className="review">
            <div className={`ref-card ${subject.frame}`}>
              <div className="shot">
                {place.src
                  ? (
                    <button
                      className="peek" title="See it full size"
                      onClick={() => setPreview({ src: place.src, label: place.name })}
                    >
                      <Img src={place.src} alt={place.name} />
                    </button>
                  )
                  : <div className="ph">not built yet</div>}
                <span className="slot">{place.name}</span>
                {photos[0]?.src && <Img className="base" src={photos[0].src} alt="" />}
                {place.approved && <span className="kept">kept</span>}
              </div>
              <div className="meta">
                <div className="nm">{place.name}</div>
                <div className="acts">
                  <button className="btn btn-sm" disabled={busy || building} onClick={() => void build()}>
                    Re-roll
                  </button>
                  <button
                    className={`btn btn-sm${place.approved ? '' : ' btn-primary'}`}
                    disabled={busy || !place.src || place.approved}
                    onClick={() => void keep()}
                  >
                    {place.approved ? 'Kept' : 'Keep'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="notice warn" style={{ marginTop: 16 }}>
{subject.warning}
          </div>

          <div className="foot">
            <button className="btn btn-primary" disabled={!place.approved} onClick={() => setStep(4)}>
              Save {subject.noun}
            </button>
            <button className="btn" onClick={() => setStep(2)}>Back</button>
            <span className="faint" style={{ fontSize: 11.5 }}>
              {place.approved ? '' : 'Keep it to finish.'}
            </span>
          </div>
        </div>
      )}

      {/* ─────────── 4 · the room ─────────── */}
      {step === 4 && (
        <div className="card">
          <div className="world-head">
            <div>
              <span className="eyebrow">{place.name}</span>
              <p className="muted" style={{ margin: '7px 0 0', fontSize: 13, maxWidth: '74ch' }}>
                Kept, and offered to any beat from now on. A shot spends one of its ten images on
                this — the rest are hers, the other half of the scene, and headroom.
              </p>
            </div>
            <span className="pill ok"><span className="dot" />kept</span>
          </div>

          <div className="wiz-split">
            <div className={`room-result ${subject.frame}`}>
              <Img src={place.src} alt={place.name} />
            </div>
            <div className="card">
              
              <div className="room-meta">
                <span>Built from</span>
                <b>{photos.length > 0 ? `${photos.length} photo${photos.length === 1 ? '' : 's'} + words` : 'words alone'}</b>
              </div>
              <div className="room-meta"><span>Used by</span><b>{place.usedCount} {place.usedCount === 1 ? 'shot' : 'shots'}</b></div>
              <div className="foot">
                <button className="btn" disabled={busy || building} onClick={() => { void build(); setStep(2); }}>
                  Rebuild from the photos
                </button>
                <button className="btn" onClick={() => setStep(1)}>Its photos</button>
              </div>
              <p className="faint" style={{ fontSize: 11.5, margin: '9px 0 0', lineHeight: 1.5 }}>
                Rebuilding replaces the picture and un-keeps it — a new one has not been looked at.
              </p>
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          addFiles(files);
        }}
      />

      <input
        ref={directRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
        onChange={(e) => {
          const file = Array.from(e.target.files ?? [])[0];
          e.target.value = '';
          if (!file) return;
          const form = new FormData();
          form.append('file', file);
          void guard(() => api.upload(`${base}/photo`, form)).then(() => setStep(4));
        }}
      />

      {preview && (
        <>
          <div className="lb-scrim" onClick={() => setPreview(null)} />
          <div className="lb" role="dialog" aria-label={preview.label}>
            <div className="lb-head">
              <span style={{ flex: 1, fontSize: 13 }}>{preview.label}</span>
              <button className="btn btn-sm" onClick={() => setPreview(null)}>Close</button>
            </div>
            <div className="lb-body">
              <Img src={preview.src} alt={preview.label} style={{ maxHeight: '78vh', width: 'auto' }} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export type { Photo };
